import { EUserAccessPart } from "@kuika/kuika-cl-model";
import * as React from "react";
import { connect } from "react-redux";
import { AiPaymentService } from "../../../../services/ai-payment-service";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { AppState } from "../../../../store/root-reducer";
import { setSettingsKeys } from "../../../../store/settings-panel/actions";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSelectionIndicator from "../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "../projects-panel/projects-left-panel.scss";

declare let window: any;

export interface ISettingsLeftPanelState {
  isAiPaymentAdmin: boolean;
}

export interface ISettingsLeftPanelProps {
  url?: string;
  setSettingsKeys?: (params: string[]) => void;
  setSettingsPanelActiveKeys?: string[];
}

// Module-level cache so remounts don't flash the menu item
let adminCache: { value: boolean; ts: number } | null = null;
const ADMIN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

class SettingsLeftPanel extends React.PureComponent<
  ISettingsLeftPanelProps & IWithHistoryProp & IWithContextProp,
  ISettingsLeftPanelState
> {
  state: ISettingsLeftPanelState = {
    isAiPaymentAdmin: adminCache && Date.now() - adminCache.ts < ADMIN_CACHE_TTL ? adminCache.value : false
  };

  async componentDidMount() {
    if (adminCache && Date.now() - adminCache.ts < ADMIN_CACHE_TTL) {
      if (this.state.isAiPaymentAdmin !== adminCache.value) {
        this.setState({ isAiPaymentAdmin: adminCache.value });
      }
      return;
    }
    try {
      const isAdmin = await AiPaymentService.CheckAdmin();
      adminCache = { value: isAdmin, ts: Date.now() };
      this.setState({ isAiPaymentAdmin: isAdmin });
    } catch {
      // silently fail — menu item stays hidden
    }
  }

  render() {
    const hasUserAccessSubscription = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditSubscription);
    const hasUserAccessEditUser = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.AddEditUser);
    const isMainAi = KMainFunctions.isMainAI();
    const url = this.props.navigator.location.pathname;

    return (
      <div className="kuika-projects__left-panel">
        <div className="kuika-selection-indicator-host--menu-items">
          <KuikaSelectionIndicator continuityKey="settings-left-panel" />
          {hasUserAccessEditUser && (
            <KuikaMenuItem
              data-testid="allUserTab"
              data-onboarding-id="settings_panel_all-users"
              title="Team"
              selected={this.props.url === "users"}
              onClick={() => this.props.navigator.gotoNewUsers()}
              leftIcon={<KuikaSvg name="users-group" />}
            />
          )}
          {hasUserAccessEditUser && (
            <KuikaMenuItem
              data-testid="rolesTab"
              data-onboarding-id="settings_panel_roles"
              title="Roles"
              selected={this.props.url === "roles"}
              onClick={() => this.props.navigator.gotoNewRoles()}
              leftIcon={<KuikaSvg name="shield-check" />}
            />
          )}
          {((this.props.context?.userInfo?.isDomainOwner && window.location.origin.includes("kuika.com")) ||
            hasUserAccessSubscription) &&
            !isMainAi && (
              <KuikaMenuItem
                data-testid="subscriptionTab"
                data-onboarding-id="settings_panel_subscription"
                title="Subscription"
                selected={this.props.url === "subscriptions" || url.includes("subscriptionPlans")}
                onClick={() => this.props.navigator.gotoSubsciptions()}
                leftIcon={<KuikaSvg name="calendar-dollar" />}
              />
            )}
          {hasUserAccessSubscription && !isMainAi && (
            <KuikaMenuItem
              data-testid="billingTab"
              data-onboarding-id="settings_panel_billing"
              title="Billing"
              selected={this.props.url === "Billing"}
              onClick={() => this.props.navigator.gotoBilling()}
              leftIcon={<KuikaSvg name="report-money" />}
            />
          )}

          <KuikaMenuItem
            data-testid="aiChatbotCreditsTab"
            title="AI Chatbot"
            selected={this.props.url === "ai-chatbot-credits"}
            onClick={() => this.props.navigator.gotoAiChatbotCredits()}
            leftIcon={<KuikaSvg name="sparkles" type="datasource-icons" width={16} height={16} />}
          />

          {this.state.isAiPaymentAdmin && (
            <KuikaMenuItem
              data-testid="aiChatbotAdminTab"
              title="AI Chatbot Admin"
              selected={this.props.url === "ai-chatbot-admin"}
              onClick={() => this.props.navigator.gotoAiChatbotAdmin()}
              leftIcon={<KuikaSvg name="shield-check" width={16} height={16} />}
            />
          )}

          {(KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username) ||
            this.props?.context.userInfo?.username === "furkan.ceylan@kuika.com" ||
            this.props?.context.userInfo?.username === "ozge.kacar@kuika.com" ||
            this.props?.context.userInfo?.username === "burcu.yazici@kuika.com" ||
            this.props?.context.userInfo?.username === "kadir.bayar@kuika.com") && (
            <KuikaMenuItem
              data-testid="onboardingTab"
              data-onboarding-id="settings_panel_onboarding"
              title="Onboarding"
              selected={this.props.url === "onboarding"}
              onClick={() => this.props.navigator.gotoOnboarding()}
              leftIcon={<KuikaSvg name="info-circle" />}
            />
          )}

          {KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username) &&
            window.location.href.includes("localhost:3000") && (
              <KuikaMenuItem
                data-testid="componentSetsTab"
                title="Component Sets"
                selected={
                  this.props.url === "settings" || this.props.url === "componentset" || url.includes("componentlist")
                }
                onClick={() => this.props.navigator.gotoComponentSet()}
                leftIcon={<KuikaSvg name="components" />}
              />
            )}

          {KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username) &&
            window.location.href.includes("localhost:3000") && (
              <KuikaMenuItem
                title="UI Kit"
                selected={this.props.url === "settings" || this.props.url === "ui-kit"}
                onClick={() => this.props.navigator.gotoUiKits()}
                leftIcon={<KuikaSvg name="components" />}
              />
            )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  setSettingsPanelActiveKeys: state.settingsPanel.setSettingsPanelActiveKeys
});

const settingsLeftPanel = withContext<ISettingsLeftPanelProps>(
  withHistory<any>(connect(mapStateToProps, { setSettingsKeys })(SettingsLeftPanel))
);
export { settingsLeftPanel as SettingsLeftPanel };
